import React from 'react';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { tomorrow } from 'react-syntax-highlighter/dist/esm/styles/prism';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';

const MarkdownRenderer = ({ content, className = '' }) => {
  const components = {
    // Custom heading renderer with anchors
    h1: ({ children, ...props }) => (
      <h1 className="text-4xl font-bold text-gray-900 mb-6 leading-tight" {...props}>
        {children}
      </h1>
    ),
    h2: ({ children, ...props }) => (
      <h2 className="text-3xl font-bold text-gray-800 mb-5 mt-8 leading-tight" {...props}>
        {children}
      </h2>
    ),
    h3: ({ children, ...props }) => (
      <h3 className="text-2xl font-bold text-gray-800 mb-4 mt-6 leading-tight" {...props}>
        {children}
      </h3>
    ),
    h4: ({ children, ...props }) => (
      <h4 className="text-xl font-bold text-gray-800 mb-3 mt-5 leading-tight" {...props}>
        {children}
      </h4>
    ),
    h5: ({ children, ...props }) => (
      <h5 className="text-lg font-bold text-gray-800 mb-3 mt-4 leading-tight" {...props}>
        {children}
      </h5>
    ),
    h6: ({ children, ...props }) => (
      <h6 className="text-base font-bold text-gray-800 mb-2 mt-3 leading-tight" {...props}>
        {children}
      </h6>
    ),
    
    // Paragraph styling
    p: ({ children, ...props }) => (
      <p className="text-gray-700 mb-4 leading-relaxed" {...props}>
        {children}
      </p>
    ),
    
    // List styling
    ul: ({ children, ...props }) => (
      <ul className="list-disc list-inside mb-4 space-y-2 text-gray-700 ml-4" {...props}>
        {children}
      </ul>
    ),
    ol: ({ children, ...props }) => (
      <ol className="list-decimal list-inside mb-4 space-y-2 text-gray-700 ml-4" {...props}>
        {children}
      </ol>
    ),
    li: ({ children, ...props }) => (
      <li className="text-gray-700 leading-relaxed" {...props}>
        {children}
      </li>
    ),
    
    // Links
    a: ({ children, href, ...props }) => (
      <a
        href={href}
        className="text-green-600 hover:text-green-800 underline font-medium"
        target={href?.startsWith('http') ? '_blank' : undefined}
        rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}
        {...props}
      >
        {children}
      </a>
    ),
    
    // Images
    img: ({ src, alt, ...props }) => (
      <div className="my-6">
        <img
          src={src}
          alt={alt}
          className="w-full h-auto rounded-lg shadow-lg"
          loading="lazy"
          {...props}
        />
        {alt && (
          <p className="text-sm text-gray-500 text-center mt-2 italic">
            {alt}
          </p>
        )}
      </div>
    ),
    
    // Blockquotes
    blockquote: ({ children, ...props }) => (
      <blockquote
        className="border-l-4 border-green-500 bg-green-50 pl-6 py-4 my-6 italic text-gray-700"
        {...props}
      >
        {children}
      </blockquote>
    ),
    
    // Code blocks
    code: ({ node, inline, className, children, ...props }) => {
      const match = /language-(\w+)/.exec(className || '');
      
      if (!inline && match) {
        return (
          <div className="my-6">
            <SyntaxHighlighter
              style={tomorrow}
              language={match[1]}
              PreTag="div"
              className="rounded-lg shadow-lg"
              {...props}
            >
              {String(children).replace(/\n$/, '')}
            </SyntaxHighlighter>
          </div>
        );
      }
      
      return (
        <code
          className="bg-gray-100 text-green-800 px-2 py-1 rounded text-sm font-mono"
          {...props}
        >
          {children}
        </code>
      );
    },
    
    // Tables
    table: ({ children, ...props }) => (
      <div className="my-6 overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 border border-gray-300 rounded-lg" {...props}>
          {children}
        </table>
      </div>
    ),
    thead: ({ children, ...props }) => (
      <thead className="bg-green-50" {...props}>
        {children}
      </thead>
    ),
    tbody: ({ children, ...props }) => (
      <tbody className="bg-white divide-y divide-gray-200" {...props}>
        {children}
      </tbody>
    ),
    th: ({ children, ...props }) => (
      <th
        className="px-6 py-3 text-left text-xs font-medium text-green-800 uppercase tracking-wider"
        {...props}
      >
        {children}
      </th>
    ),
    td: ({ children, ...props }) => (
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700" {...props}>
        {children}
      </td>
    ),
    
    // Horizontal rule
    hr: ({ ...props }) => (
      <hr className="my-8 border-t-2 border-gray-200" {...props} />
    ),
    
    // Strong and emphasis
    strong: ({ children, ...props }) => (
      <strong className="font-bold text-gray-900" {...props}>
        {children}
      </strong>
    ),
    em: ({ children, ...props }) => (
      <em className="italic text-gray-800" {...props}>
        {children}
      </em>
    ),
  };

  return (
    <div className={`prose prose-green max-w-none ${className}`}>
      <ReactMarkdown
        components={components}
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw]}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};

export default MarkdownRenderer;
